/**
 * BlockPage - Dedicated page for viewing block details and standings.
 *
 * Route: /admin/blocks/:blockId
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { liveApi, blocksApi } from '@/api/client';
import { EventLog } from '@/components/EventLog';
import { GamesListTable } from '@/components/GamesListTable';
import type { BlockData, Game } from '@/types';
import { generatePaperId, getGameStatus } from '@/types';

export function BlockPage() {
  const { blockId } = useParams<{ blockId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const paperId = blockId ? parseInt(blockId, 10) : null;

  // Clear games dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const handleGameClick = (gameId: number) => {
    navigate(`/admin/games/${gameId}`);
  };

  const handleTeamClick = (teamId: number) => {
    navigate(`/admin/teams/${teamId}`);
  };

  const handleCourtClick = (courtId: number) => {
    navigate(`/admin/courts/${courtId}`);
  };

  // Fetch block data
  const {
    data: blockData,
    isLoading,
    error,
  } = useQuery<BlockData>({
    queryKey: ['block', paperId],
    queryFn: () => liveApi.getBlock(paperId!),
    enabled: paperId !== null,
  });

  // Clear games mutation
  const clearGamesMutation = useMutation({
    mutationFn: () => blocksApi.clearGames(paperId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block', paperId] });
      setClearDialogOpen(false);
    },
  });

  // Combine all games from block data
  const allGames = useMemo<Game[]>(() => {
    if (!blockData) return [];
    return [
      ...blockData.games.finished,
      ...blockData.games.ongoing,
      ...blockData.games.upcoming,
    ];
  }, [blockData]);

  // Calculate progress
  const progress = useMemo(() => {
    if (!allGames.length) return { finished: 0, total: 0, inProgress: 0 };
    let finished = 0;
    let inProgress = 0;
    for (const game of allGames) {
      const status = getGameStatus(game);
      if (status === 'finished') finished++;
      else if (status === 'in_progress') inProgress++;
    }
    return { finished, total: allGames.length, inProgress };
  }, [allGames]);

  // Get league from first game
  const league = allGames.length > 0 ? allGames[0].league_id : null;
  const round = allGames.length > 0 ? allGames[0].round : null;

  if (!paperId) {
    return (
      <Box>
        <Alert severity="error">Invalid block ID</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/blocks')} sx={{ mt: 2 }}>
          Back to Blocks
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
          Admin
        </Link>
        <Link component={RouterLink} to="/admin/blocks" underline="hover" color="inherit">
          Blocks
        </Link>
        <Typography color="text.primary">Block {generatePaperId(paperId)}</Typography>
      </Breadcrumbs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load block details
          </Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/blocks')}>
            Back to Blocks
          </Button>
        </Box>
      ) : blockData ? (
        <>
          {/* Header with Progress */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Typography variant="h4" fontWeight={700}>
              Block {generatePaperId(paperId)}
            </Typography>
            {league && (
              <Chip
                label={league.toUpperCase()}
                color={league === 'yle' ? 'primary' : league === 'pro' ? 'secondary' : 'default'}
              />
            )}
            {round !== null && <Chip label={`Round ${round}`} variant="outlined" />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                {progress.finished}/{progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.total > 0 ? (progress.finished / progress.total) * 100 : 0}
                sx={{ height: 8, borderRadius: 4, width: 100 }}
              />
              {progress.inProgress > 0 && (
                <Chip
                  label={`${progress.inProgress} in progress`}
                  size="small"
                  color="primary"
                />
              )}
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<ClearAllIcon />}
                onClick={() => setClearDialogOpen(true)}
                disabled={allGames.length === 0}
              >
                Clear Games
              </Button>
            </Box>
          </Box>

          {/* Referee Info */}
          {blockData.referee && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={500}>
                  {blockData.referee.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({blockData.referee.username})
                </Typography>
              </Box>
              {blockData.referee.phone && (
                <TextField
                  size="small"
                  value={blockData.referee.phone}
                  slotProps={{
                    input: {
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }
                  }}
                  sx={{ width: 180, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.875rem' } }}
                />
              )}
              <Chip label={blockData.referee.status} size="small" variant="outlined" />
            </Paper>
          )}

          {/* Standings Table */}
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Standings
          </Typography>

          {blockData.points.length > 0 ? (
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    '& th, & td': {
                      px: 2,
                      py: 1,
                      textAlign: 'left',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                    '& th': {
                      fontWeight: 600,
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Team</th>
                      <th style={{ textAlign: 'center' }}>P</th>
                      <th style={{ textAlign: 'center' }}>W</th>
                      <th style={{ textAlign: 'center' }}>D</th>
                      <th style={{ textAlign: 'center' }}>L</th>
                      <th style={{ textAlign: 'center' }}>Pts</th>
                      <th style={{ textAlign: 'right' }}>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockData.points
                      .map((team, index) => (
                        <tr
                          key={team.team_id}
                          style={{
                            backgroundColor: index === 0 ? 'rgba(46, 125, 50, 0.1)' : undefined,
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <Typography
                              variant="body2"
                              fontWeight={index === 0 ? 700 : 400}
                              color={index === 0 ? 'success.main' : 'primary.main'}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => handleTeamClick(team.team_id)}
                            >
                              {team.team_name}
                            </Typography>
                          </td>
                          <td style={{ textAlign: 'center' }}>{team.games_played}</td>
                          <td style={{ textAlign: 'center' }}>{team.wins}</td>
                          <td style={{ textAlign: 'center' }}>{team.ties}</td>
                          <td style={{ textAlign: 'center' }}>{team.losses}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{team.team_points}</td>
                          <td style={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontFamily="monospace">
                              {team.average?.toFixed(1) ?? '-'}
                            </Typography>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No standings available yet</Typography>
            </Paper>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            P = Played, W = Win (2pts), D = Draw (1pt), L = Loss (0pts)
          </Typography>

          {/* Games List */}
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
            Games ({allGames.length})
          </Typography>

          {allGames.length > 0 ? (
            <Paper sx={{ height: 400, overflow: 'hidden', mb: 3 }}>
              <GamesListTable
                games={allGames}
                columns={{ time: true, actualTime: true, block: false, court: true, teams: true, score: true, status: true }}
                onGameClick={handleGameClick}
                onTeamClick={handleTeamClick}
                onCourtClick={handleCourtClick}
                variant="elevation"
                maxHeight={400}
              />
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
              <Typography color="text.secondary">No games scheduled</Typography>
            </Paper>
          )}

          {/* Winner's Path / Playoff Pathway */}
          {blockData.pathway && blockData.pathway.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Winner's Path
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 80 }}></TableCell>
                    {blockData.pathway.map((step) => (
                      <TableCell key={step.paper_id} align="center" sx={{ fontWeight: 500 }}>
                        {step.schedule_start} – {step.schedule_end}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Block</TableCell>
                    {blockData.pathway.map((step) => (
                      <TableCell
                        key={step.paper_id}
                        align="center"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/admin/blocks/${step.paper_id}`)}
                      >
                        <Typography
                          variant="body2"
                          color="primary.main"
                          sx={{ '&:hover': { textDecoration: 'underline' } }}
                        >
                          {step.paper_id}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Court</TableCell>
                    {blockData.pathway.map((step) => (
                      <TableCell
                        key={step.paper_id}
                        align="center"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/admin/courts/${step.court_id}`)}
                      >
                        <Typography
                          variant="body2"
                          color="primary.main"
                          sx={{ '&:hover': { textDecoration: 'underline' } }}
                        >
                          {step.court_id}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Event Log */}
          <EventLog params={{ block_id: paperId }} title="Block Event Log" />

          {/* Clear Games Confirmation Dialog */}
          <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
            <DialogTitle>Clear All Games</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to clear all {allGames.length} games in this block?
                This will reset all game states and scores. This action cannot be undone.
              </DialogContentText>
              {clearGamesMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to clear games
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => clearGamesMutation.mutate()}
                disabled={clearGamesMutation.isPending}
              >
                {clearGamesMutation.isPending ? 'Clearing...' : 'Clear All'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Box>
  );
}

